import React, { useCallback } from 'react';
import GlobalError from '../components/GlobalError';
import useDataFetch from '../hooks/useDataFetch';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import Pagination from '../components/Pagination';
import { Refresh, ArrowBackIosNew } from '@mui/icons-material';
import theme from '../theme';
import useSocket from '../hooks/useSocket';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Stack, IconButton,
  Chip
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const PickingPage = ({ warehouseId }) => {
  const navigate = useNavigate();
  const {
    dataset,
    setDataset,
    loading,
    error,
    currentPage,
    totalPages,
    handlePageChange,
    refetch
  } = useDataFetch('/order', {
    is_completed: false,
    order_numbers: '',
    client_name: '',
    dates: '',
    order_status: [5],
    warehouse_id: warehouseId,
  });
  const handlePickingSent = useCallback((payload) => {
    refetch();
  }, [refetch]);

  const handlePickingStarted = useCallback((payload) => {
    setDataset(prev =>
      prev.map(item =>
        item.order_id === payload.order_id
          ? { ...item, status: 'WORKING' }
          : item
      )
    );
  }, [setDataset]);

  const handlePickingCompleted = useCallback((payload) => {
    setDataset(prev =>
      prev.filter(item => item.order_id !== payload.order_id)
    );
  }, [setDataset]);

  useSocket(warehouseId, {
    'order:picking-sent': handlePickingSent,
    'order:picking-started': handlePickingStarted,
    'order:picking-completed': handlePickingCompleted,
  });

  const PICKING_STATUS_MAP = {
    READY: {
      label: '開始前',
      color: 'primary',
    },
    WORKING: {
      label: '進行中',
      color: 'success',
    },
    DONE: {
      label: '完了',
      color: 'default',
    },
    CANCELLED: {
      label: '取消',
      color: 'error',
    },
  };

  if (error) return <GlobalError errorMessage={error} />;

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton onClick={() => navigate('/')}>
          <ArrowBackIosNew />
        </IconButton>
        <Typography variant="h1" fontWeight="bold">ピッキングリスト</Typography>
      </Stack >

      {/* リスト */}
      <Stack spacing={1}>
        {dataset.map((item) => (
          <Card key={item.order_id}>
            <CardActionArea
              component={RouterLink}
              to={`/picking/${item.order_id}`}
            >
              <CardContent
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box>
                  <Chip
                    label={PICKING_STATUS_MAP[item.status]?.label ?? item.status}
                    color={PICKING_STATUS_MAP[item.status]?.color ?? 'default'}
                    size="small"
                    sx={{ mb: 1 }}
                  />

                  <Typography fontWeight="bold">{item.order_number}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {item.client_name}
                    {item.branch_name && ` / ${item.branch_name}`}
                  </Typography>
                </Box>

                <ChevronRightIcon color="action" />
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Stack>

      {/* ページネーション */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
      <Box
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
        }}
      >
        <IconButton
          onClick={() => refetch()}
          sx={{
            background: `linear-gradient(to right, #1CABF7, ${theme.palette.primary.main})`,
            color: '#fff',
            width: 48,
            height: 48,
            borderRadius: '50%',
          }}
        >
          <Refresh />
        </IconButton>
      </Box>
    </>
  );
};

export default PickingPage;
